import { useState } from 'react';
import Button from '../../../components/ui/Button';
import type { LoanRequestRow } from '../../../services/loanService';

interface LoanRequestCardProps {
  request: LoanRequestRow;
  currency: string;
  canApprove: boolean;
  onApprove: (requestId: string) => Promise<void>;
  onReject: (requestId: string, notes: string) => Promise<void>;
}

const LoanRequestCard = ({
  request,
  currency,
  canApprove,
  onApprove,
  onReject,
}: LoanRequestCardProps) => {
  const [loading, setLoading] = useState(false);

  const memberName =
    request.member?.full_name || request.member?.email || 'Unknown member';

  const requesterName =
    request.requester?.full_name || request.requester?.email || null;

  const isRequestedOnBehalf = request.requested_by && request.requested_by !== request.member_id;

  const handleApprove = async () => {
    if (!window.confirm(`Approve loan request for ${memberName}?`)) return;
    setLoading(true);
    try {
      await onApprove(request.id);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    const notes = window.prompt('Enter rejection reason:');
    if (!notes?.trim()) return;
    setLoading(true);
    try {
      await onReject(request.id, notes.trim());
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-md p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-foreground">{memberName}</h3>
          <p className="text-xs text-muted-foreground capitalize">{request.purpose}</p>
          {isRequestedOnBehalf && requesterName && (
            <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
              <span className="inline-block w-1 h-1 bg-blue-600 rounded-full"></span>
              Requested by {requesterName} on behalf of member
            </p>
          )}
        </div>
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-warning/10 text-warning">
          {request.status}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground mb-4">
        <div>
          <span className="block text-foreground font-medium">
            {Number(request.requested_amount).toLocaleString()} {currency}
          </span>
          Requested
        </div>
        <div>
          <span className="block text-foreground font-medium">
            {request.repayment_months} months
          </span>
          Repayment period
        </div>
        <div>
          <span className="block text-foreground font-medium">
            {Number(request.total_repayment_amount).toLocaleString()} {currency}
          </span>
          Total repayment
        </div>
        <div>
          <span className="block text-foreground font-medium">
            {Number(request.monthly_payment_amount).toLocaleString()} {currency}
          </span>
          Monthly payment
        </div>
      </div>

      {request.purpose_notes && (
        <p className="text-xs text-muted-foreground mb-4">{request.purpose_notes}</p>
      )}

      {canApprove && request.status === 'pending' && (
        <div className="flex gap-2">
          <Button
            variant="default"
            size="sm"
            onClick={handleApprove}
            disabled={loading}
            loading={loading}
            fullWidth
          >
            Approve
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleReject}
            disabled={loading}
            fullWidth
          >
            Reject
          </Button>
        </div>
      )}
    </div>
  );
};

export default LoanRequestCard;
